import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from "@mui/material";
import { TFaturaFechamentoData, TFaturaNotificacao } from "../../../../../../../../types/api/financeiro/detalhar-fechamentos-conta-corrente";
import PaperDraggable from "../../../../../../../../components/PaperDraggable";
import NotificacaoFatura from "./NotificacaoFatura";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../../lib/axios";
import { TDataEnviarFaturas, TEnviarFaturas } from "../../DialogEnviarFaturas";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import { TConfigWhatsApp } from "../../../../../../../Configuracoes/IntegracaoWhatsapp";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { formatarTelefone } from "../../../../../../../../helpers/masks";
import { desformatarTelefone } from "../../../../../../../../helpers/unmask";
export default function DialogInfoNotificacao({
    id,
    open,
    notificacoes,
    fatura,
    onClose
}: DialogInfoNotificacaoProps) {

    const queryClient = useQueryClient();
    //Estado inicial do React Hook Form
    const methods = useForm<{ fatura: TEnviarFaturas }>({
        defaultValues: {
            fatura: {
                ...fatura,
                email: fatura.email,
                telefone: fatura.telefone ? formatarTelefone(fatura.telefone) : undefined,
            }
        }
    });
    const { control, handleSubmit, setError } = methods

    //Querys e mutations
    const { data, isLoading: isLoadingConfiguracoes } = useQuery({
        queryKey: ["configuracoes-whatsapp"],
        queryFn: () => axiosClient.get("/configuracoes", {
            params: {
                chave: 'config_whatsapp'
            }
        })
    });

    const { mutate: enviarFaturas, isLoading } = useMutation({
        mutationKey: "enviar-faturas",
        mutationFn: (data: TDataEnviarFaturas[]) =>
            axiosClient.post("/financeiro/conta-corrente-consumo/enviar-faturas", {
                faturas: data
            }),
        onSuccess: () => {
            queryClient.invalidateQueries(`detalhar-fechamento-${id}`)
            enqueueSnackbar("Faturas enviadas com sucesso", { variant: 'success' })
            onClose();
        },
        onError: (error) => {
            console.error("Erro ao enviar o e-mail:", error);
        },
    });
    //

    //Funções manipuladoras
    const handleEnviar = (data: { fatura: TEnviarFaturas }) => {
        if (!validateRequest(data.fatura)) {
            enqueueSnackbar("Verifique os campos não preenchidos", { variant: 'warning' });
            return;
        }

        // Formatação dos dados para envio
        const params: TDataEnviarFaturas = {
            id: data.fatura.id,
            telefone: !data.fatura.telefone ? null : desformatarTelefone(data.fatura.telefone),
            email: !data.fatura.email ? null : data.fatura.email
        }

        enviarFaturas([params])
    };
    //

    // Validações
    const dataConfigWhatsapp: TConfigWhatsApp | null = useMemo(() => {
        // Se não vier nada retorna null;
        if (!data?.data) return null;

        // Verificação se possui os campos numero e token
        const isValid = Object.values(data.data).every(
            (item: any) => "numero" in item && "token" in item
        );

        // Se for valido retorna o data
        return isValid ? data.data : null;
    }, [data]);

    const validateRequest = (fatura: TEnviarFaturas) => {
        let isValid = true;

        if (fatura.email === "" && fatura.telefone === "") {
            setError(`fatura.telefone`, {
                type: 'error'
            })
            setError(`fatura.email`, {
                type: 'error'
            })
            isValid = false;
        }

        return isValid;
    }

    const validacaoInicial = useMemo(() => {
        if (!notificacoes || notificacoes.length === 0) return null;

        const hasEmailNotification = notificacoes.some(notificacao => notificacao.tipo === 'Email');
        const hasWhatsAppNotification = notificacoes.some(notificacao => notificacao.tipo === 'WhatsApp');

        if (hasEmailNotification && hasWhatsAppNotification) return true;
        if (!hasEmailNotification && !hasWhatsAppNotification) return null;

        if (hasEmailNotification) {
            return (
                <>
                <Typography variant="h6">
                        Enviar fatura por WhatsApp
                    </Typography>
                <Controller
                    name={`fatura.telefone`}
                    control={control}
                    rules={{
                        pattern: {
                            value: /^\(\d{2}\) \d{4,5}-\d{4}$/,
                            message: "Telefone inválido"
                        }
                    }}
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            {...field}
                            fullWidth
                            label="WhatsApp"
                            error={!!error}
                            onChange={(e) => {
                                const newValue = formatarTelefone(e.target.value)
                                field.onChange(newValue)
                            }}
                            helperText={error?.message}
                            type="tel"
                            disabled={!dataConfigWhatsapp}
                        />
                    )}
                />
                </>
            );
        }

        if (hasWhatsAppNotification) {
            return (
                <>
                    <Typography variant="h6">
                        Enviar fatura por Email
                    </Typography>
                    <Controller
                        name={`fatura.email`}
                        control={control}
                        rules={{
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: "Email inválido"
                            }
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="E-mail"
                                error={!!error}
                                onChange={(e) => {
                                    field.onChange(e.target.value)
                                }}
                                helperText={error?.message}
                            />
                        )}
                    />
                </>
            );
        }

        return null;
    }, [notificacoes, control, dataConfigWhatsapp]);

    //

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="sm"
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Notificações</DialogTitle>
                <DialogContent>
                    {
                        !validacaoInicial &&
                        <Grid container spacing={1} mt={1}>
                            <Grid size={{xs: 6}}>
                                <Controller
                                    name={`fatura.telefone`}
                                    control={control}
                                    rules={{
                                        pattern: {
                                            value: /^\(\d{2}\) \d{4,5}-\d{4}$/,
                                            message: "Telefone inválido"
                                        }
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="WhatsApp"
                                            error={!!error}
                                            onChange={(e) => {
                                                const newValue = formatarTelefone(e.target.value)
                                                field.onChange(newValue)
                                            }}
                                            helperText={error?.message}
                                            type="tel"
                                            disabled={!dataConfigWhatsapp}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{xs: 6}}>
                                <Controller
                                    name={`fatura.email`}
                                    control={control}
                                    rules={{
                                        pattern: {
                                            value: /^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/,
                                            message: 'E-mail inválido'
                                        }
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="E-mail"
                                            onChange={(e) => field.onChange(e.target.value)}
                                            error={!!error}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    }
                    {
                        notificacoes && notificacoes.length > 0 &&
                        notificacoes?.map((notificacao, index) => {
                            if (notificacao.tipo === 'Email') {
                                return <NotificacaoFatura
                                    {...{ validacaoInicial }}
                                    key={index} notificacao={notificacao}
                                    tipo={notificacao.tipo}
                                    onClose={onClose}
                                />
                            }
                            if (notificacao.tipo === 'WhatsApp') {
                                return <NotificacaoFatura
                                    {...{ validacaoInicial }}
                                    key={index}
                                    notificacao={notificacao}
                                    tipo={notificacao.tipo}
                                    onClose={onClose}
                                />
                            }
                            else return <></>
                        })
                    }
                </DialogContent>
                <DialogActions>
                    <Button disabled={isLoading} onClick={onClose} color="primary">
                        Fechar
                    </Button>
                    {
                        (!notificacoes || validacaoInicial !== true) &&
                        <Button
                            disabled={isLoading}
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit(handleEnviar)}
                        >
                            {
                                isLoading &&
                                <CircularProgress size={20} sx={{ marginRight: 1 }} />
                            }
                            Enviar
                        </Button>
                    }
                </DialogActions>
            </Dialog>
        </FormProvider>
    )
}

interface DialogInfoNotificacaoProps {
    id: number;
    fatura: TFaturaFechamentoData
    open: boolean
    notificacoes: TFaturaNotificacao[] | null
    onClose: () => void
}



